/**
 * Composant d'affichage des informations de livraison
 * Affiche l'adresse, le type, le prix de livraison
 * Créé le 21 juillet 2026
 */

import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Divider
} from '@mui/material';
import {
  LocalShipping as LocalShippingIcon,
  LocationOn as LocationIcon,
  Payment as PaymentIcon,
  Speed as SpeedIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import { formatCurrency } from '../../constants/dashboardConstants';
import { getDeliveryType, getDeliveryAddress } from '../../utils/deliveryUtils';

const DeliveryInfoDisplay = ({ reservation }) => {
  const { t } = useTranslation(['delivery', 'common']);

  const deliveryType = getDeliveryType(reservation);
  const deliveryAddress = getDeliveryAddress(reservation);
  const deliveryFee = reservation.extra_fees?.delivery_fee_cents || 0;
  const currency = reservation.currency || 'EUR';

  return (
    <Box sx={{ mb: 3 }}>
      <Typography 
        variant="subtitle1" 
        gutterBottom 
        sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <LocalShippingIcon color="primary" />
        {t('deliveryInformation')}
      </Typography>

      <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
        {/* Type de livraison */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {t('deliveryType')}
          </Typography>
          <Chip
            icon={deliveryType === 'express' ? <SpeedIcon /> : <LocalShippingIcon />}
            label={deliveryType === 'express' 
              ? t('expressDelivery') 
              : t('standardDelivery')
            }
            color={deliveryType === 'express' ? 'error' : 'primary'}
            size="small"
            sx={{ fontWeight: 500 }}
          />
        </Box>

        {/* Adresse de livraison */}
        {deliveryAddress && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <LocationIcon sx={{ fontSize: 16 }} />
                {t('deliveryAddress')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.6 }}>
                {deliveryAddress.street && <>{deliveryAddress.street}<br /></>}
                {deliveryAddress.postalCode && deliveryAddress.city && (
                  <>{deliveryAddress.postalCode} {deliveryAddress.city}<br /></>
                )}
                {deliveryAddress.country && <>{deliveryAddress.country}</>}
              </Typography>
            </Box>
          </>
        )}

        {/* Frais de livraison */}
        <Divider sx={{ my: 2 }} />
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <PaymentIcon sx={{ fontSize: 16 }} />
            {t('deliveryFee')}
          </Typography>
          <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
            {deliveryFee === 0 
              ? t('freeDelivery') 
              : formatCurrency(deliveryFee / 100, currency)
            }
          </Typography>
        </Box>
      </Paper>

      {/* Demandes spéciales du client */}
      {reservation.special_requests && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2.5, 
            mt: 2,
            backgroundColor: '#fffbeb',
            border: '1px solid #fde68a',
            borderLeft: '4px solid #f59e0b'
          }}
        >
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 600, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              mb: 1.5,
              color: '#92400e'
            }}
          >
            <MessageIcon sx={{ fontSize: 20 }} />
            {t('specialRequests', 'Demandes spéciales du client')}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              lineHeight: 1.7,
              color: '#78350f',
              fontWeight: 500,
              whiteSpace: 'pre-wrap'
            }}
          >
            {reservation.special_requests}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default DeliveryInfoDisplay;
